'use client'

import dynamic from 'next/dynamic'
import { Link } from '@/i18n/navigation'
import SearchBar from './SearchBar'
import LanguageSwitcher from './LanguageSwitcher'
import { useState, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { useTheme } from 'next-themes'
import { t } from '@/i18n/translations'
import type { SidebarData } from '@/services/sidebarApi'

// U2 (Temmuz 2026 denetimi): `ssr: false` kaldırıldı. O bayrak yüzünden her sayfada
// BAILOUT_TO_CLIENT_SIDE_RENDERING işareti çıkıyor, şeritler ilk HTML'de boş kutu
// olarak geliyordu. Artık sunucuda da render ediliyorlar; veriyi layout prop olarak
// veriyor. Sabit yükseklikli `loading` iskeleti CLS'e karşı korunuyor.
const BreakingNews = dynamic(() => import('./BreakingNews'), {
  loading: () => <div style={{ backgroundColor: '#A81E1E', height: 40, minHeight: 40, contain: 'strict' }} />,
})
const TickerBar = dynamic(() => import('./TickerBar'), {
  loading: () => <div style={{ height: 46, minHeight: 46, contain: 'strict' }} className="md:!h-[72px] md:!min-h-[72px]" />,
})

const navKeys: { key: string; href: '/' | '/stocks' | '/crypto' | '/news' | '/guides' | '/brokers' }[] = [
  { key: 'nav.markets', href: '/' },
  { key: 'nav.stocks', href: '/stocks' },
  { key: 'nav.crypto', href: '/crypto' },
  { key: 'nav.news', href: '/news' },
  { key: 'nav.guides', href: '/guides' },
  { key: 'nav.brokers', href: '/brokers' },
]

type HeaderProps = {
  /** Sunucuda çekilen kenar çubuğu verisi — ticker ve son dakika şeridini tohumlar (U2). */
  initialSidebar?: SidebarData | null
}

export default function Header({ initialSidebar }: HeaderProps) {
  const lang = useLocale()
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const isDark = mounted && theme === 'dark'

  return (
    <>
      <header className="relative z-50">
        <div style={{ backgroundColor: 'var(--bg-card)' }}>
          {/* Top: Logo + Nav + Search + Lang */}
          <div className="border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="max-w-7xl mx-auto px-4 h-14 md:h-16 flex items-center justify-between">
              {/* Logo */}
              <Link href="/" title={t('nav.home', lang)} className="flex items-center gap-3 no-underline shrink-0">
                {/*
                  P1/ONCEKI-8 (Temmuz 2026 denetimi): logo artık GERÇEK vektör SVG.
                  SVG çözünürlükten bağımsız olduğu için /_next/image ölçeklemesine
                  ihtiyaç yok — bu yüzden next/image değil düz <img> kullanılır (T4'ün
                  çözdüğü tek-çözünürlük sorunu vektörde kavramsal olarak yoktur).
                  width/height CLS'i korur. PNG'ler schema.org logo alanı için duruyor.

                  E1 / Ağustos 2026 durum raporu (madde 10): bir ara İKİ logo da
                  fetchPriority="high" taşıyordu, sonra yalnızca açık tema logosunda
                  bırakıldı. İkisi de tutarsız: tarayıcı hangi temanın aktif olduğunu
                  indirme anında bilmediği için, koyu tema kullanıcısında GÖRÜNMEYEN
                  dosya önceliklendirilmiş oluyordu. Logolar küçük vektör (SVG) olduğu
                  için yüksek öncelik kazanç sağlamıyor; ikisinden de kaldırıldı.
                  Böylece davranış her iki temada aynı.
                */}
                <img
                  src="/logo.svg"
                  alt="Borsaya"
                  width={228}
                  height={300}
                  className="h-7 md:h-10 w-auto dark:hidden"
                />
                <img
                  src="/logo_dark.svg"
                  alt="Borsaya"
                  /* Gerçek boyut 713×492; kodda 300×88 yazıyordu — yanlış en-boy
                     oranı koyu temada yükleme anında kayma (CLS) üretiyordu. */
                  width={713}
                  height={492}
                  className="h-7 md:h-10 w-auto hidden dark:block"
                />
              </Link>

              {/* Right side: Nav (desktop) + Actions (tüm ekranlar) */}
              <div className="flex items-center gap-1">
                <nav className="hidden md:flex items-center gap-1">
                  {navKeys.map((item) => (
                    <Link
                      key={item.key}
                      href={item.href}
                      title={t(item.key, lang)}
                      className="px-4 py-2 rounded-lg text-sm font-medium no-underline transition-colors duration-200 hover:opacity-80"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {t(item.key, lang)}
                    </Link>
                  ))}
                </nav>
                <div className="hidden md:block w-px h-5 mx-2" style={{ backgroundColor: 'var(--border-color)' }} />
                <div className="hidden md:block">
                  <SearchBar />
                </div>
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  aria-label={t('aria.toggleTheme', lang)}
                  className="flex items-center justify-center w-10 h-10 rounded-full border-none cursor-pointer transition-all duration-200 active:scale-95"
                  style={{ background: 'transparent', color: 'var(--text-muted)' }}
                >
                  {isDark ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="5" />
                      <line x1="12" y1="1" x2="12" y2="3" />
                      <line x1="12" y1="21" x2="12" y2="23" />
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                      <line x1="1" y1="12" x2="3" y2="12" />
                      <line x1="21" y1="12" x2="23" y2="12" />
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                  )}
                </button>
                <LanguageSwitcher />
              </div>
            </div>
          </div>

          {/* Breaking News — fixed height to prevent CLS */}
          <div style={{ height: 40, overflow: 'hidden', contain: 'strict' }}>
            <BreakingNews initialData={initialSidebar} />
          </div>

          {/* Ticker Bar — fixed height to prevent CLS */}
          <div className="border-b h-[46px] md:h-[72px]" style={{ borderColor: 'var(--border-color)', overflow: 'hidden', contain: 'strict' }}>
            <div className="max-w-7xl mx-auto h-full">
              <TickerBar initialData={initialSidebar} />
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
